import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Dimensions, Easing, Image, Platform, StyleSheet, View, ViewStyle } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors } from '../theme';
import { useTheme } from '../theme/useTheme';

interface Props {
  children?: React.ReactNode;
  style?: ViewStyle | ViewStyle[];
  appearanceAware?: boolean;
}

const { width, height } = Dimensions.get('window');

const DOTS = Array.from({ length: 11 * 28 }, (_, index) => {
  const columns = 11;
  return {
    left: ((index % columns) * 22) + 4,
    top: (Math.floor(index / columns) * 22) + 4,
  };
});

// Memoized with zero props so these expensive, purely-decorative subtrees
// (308 dot Views in IdentityLayer) bail out of re-rendering whenever
// TonyteBackground's own function body re-runs due to a `children` change,
// instead of reconciling the whole static background on every parent render.
const AmbientGlowSystem = React.memo(() => (
  <>
    <LinearGradient
      pointerEvents="none"
      colors={['#07101d', '#0a0e17', '#050812']}
      locations={[0, 0.5, 1]}
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      style={StyleSheet.absoluteFill}
    />

    {Platform.OS === 'ios' ? (
      <>
        <View pointerEvents="none" style={styles.iosTealWash} />
        <View pointerEvents="none" style={styles.iosNavyWash} />
        <View pointerEvents="none" style={styles.iosTopSheen} />
      </>
    ) : (
      <>
        <View pointerEvents="none" style={styles.androidTopGlow} />
        <View pointerEvents="none" style={styles.androidTopDepth} />
        <View pointerEvents="none" style={styles.androidBottomGlow} />
        <View pointerEvents="none" style={styles.androidBottomTealLift} />
      </>
    )}
  </>
));

const IdentityLayer = React.memo(() => (
  <>

    <View pointerEvents="none" style={styles.ambientLayer}>
      <View style={styles.ambientTeal} />
      <View style={styles.ambientOrange} />
      <LinearGradient
        colors={['rgba(161,140,209,0.08)', 'rgba(161,140,209,0)']}
        locations={[0, 0.48]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
    </View>

    <View pointerEvents="none" style={styles.dotsLayer}>
      {DOTS.map((dot, index) => (
        <View key={index} style={[styles.dot, dot]} />
      ))}
    </View>

    <View pointerEvents="none" style={styles.route} />
    <LinearGradient
      pointerEvents="none"
      colors={['#26d0ce', '#f27d26']}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={styles.routeEnd}
    />

    <Icon
      pointerEvents="none"
      name="map-pin"
      size={20}
      color="rgba(242,125,38,0.50)"
      style={styles.floatPin}
    />
    <Icon
      pointerEvents="none"
      name="compass"
      size={22}
      color="rgba(38,208,206,0.48)"
      style={styles.floatCompass}
    />

    <LinearGradient
      pointerEvents="none"
      colors={['rgba(0,0,0,0.16)', 'rgba(0,0,0,0.26)', 'rgba(0,0,0,0.48)']}
      locations={[0, 0.52, 1]}
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      style={StyleSheet.absoluteFill}
    />
    <View pointerEvents="none" style={styles.centerVignette} />
  </>
));

const DaylightBackdrop = React.memo(() => {
  const { theme } = useTheme();
  return (
    <>
      <LinearGradient
        pointerEvents="none"
        colors={theme.gradients.background}
        locations={[0, 0.52, 1]}
        style={StyleSheet.absoluteFill}
      />
      <View pointerEvents="none" style={daylightStyles.tealWash} />
      <View pointerEvents="none" style={daylightStyles.orangeWash} />
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(255,255,255,0.20)', 'rgba(238,224,205,0.04)', 'rgba(177,139,101,0.055)']}
        locations={[0, 0.56, 1]}
        start={{ x: 0.1, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View pointerEvents="none" style={daylightStyles.dotsLayer}>
        {DOTS.map((dot, index) => (
          <View key={index} style={[styles.dot, daylightStyles.dot, dot]} />
        ))}
      </View>
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(64,164,159,0.045)', 'rgba(64,164,159,0.012)', 'rgba(255,255,255,0)']}
        style={daylightStyles.orbitLarge}
      />
      <LinearGradient
        pointerEvents="none"
        colors={['rgba(211,144,83,0.045)', 'rgba(211,144,83,0.012)', 'rgba(255,255,255,0)']}
        style={daylightStyles.orbitSmall}
      />
    </>
  );
});

const EFFECT_PARTICLES = Array.from({ length: 16 }, (_, index) => ({
  left: `${4 + ((index * 31) % 92)}%` as `${number}%`,
  top: `${(index * 19) % 88}%` as `${number}%`,
  size: 3 + (index % 4),
  delay: (index % 8) / 8,
}));

const SeasonalLayer = React.memo(() => {
  const { theme, seasonalTheme, seasonalAnimationsEnabled } = useTheme();
  const progress = useRef(new Animated.Value(0)).current;
  const variant = seasonalTheme ? (theme.isDark ? seasonalTheme.dark : seasonalTheme.light) : null;
  const intensity = seasonalTheme?.effect.intensity ?? 0;
  const particleCount = Math.round(4 + intensity * 12);

  useEffect(() => {
    progress.stopAnimation();
    progress.setValue(0);
    if (!seasonalTheme || seasonalTheme.effect.type === 'none' || !seasonalAnimationsEnabled) return undefined;
    const loop = Animated.loop(Animated.timing(progress, {
      toValue: 1,
      duration: 14000 - Math.round(intensity * 4000),
      easing: Easing.linear,
      useNativeDriver: true,
    }));
    loop.start();
    return () => loop.stop();
  }, [intensity, progress, seasonalAnimationsEnabled, seasonalTheme]);

  const particles = useMemo(() => EFFECT_PARTICLES.slice(0, particleCount), [particleCount]);
  if (!seasonalTheme || !variant) return null;
  const { assets } = variant;
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      {assets?.backgroundUrl ? <Image source={{ uri: assets.backgroundUrl }} resizeMode="cover" style={seasonalStyles.backgroundArtwork} /> : null}
      {assets?.headerUrl ? <Image source={{ uri: assets.headerUrl }} resizeMode="cover" style={seasonalStyles.headerArtwork} /> : null}
      {assets?.illustrationUrl ? <Image source={{ uri: assets.illustrationUrl }} resizeMode="contain" style={seasonalStyles.illustration} /> : null}
      {seasonalTheme.effect.type !== 'none' ? particles.map((particle, index) => {
        const translateY = seasonalAnimationsEnabled ? progress.interpolate({ inputRange: [0, particle.delay, 1], outputRange: [-18, -18, height + 24] }) : 0;
        const opacity = seasonalTheme.effect.type === 'confetti' ? 0.34 : 0.22 + intensity * 0.22;
        return (
          <Animated.View
            key={`${seasonalTheme.id}-${index}`}
            style={[
              seasonalStyles.particle,
              {
                left: particle.left,
                top: particle.top,
                width: particle.size,
                height: seasonalTheme.effect.type === 'confetti' ? particle.size * 2 : particle.size,
                borderRadius: seasonalTheme.effect.type === 'confetti' ? 1 : particle.size / 2,
                backgroundColor: index % 2 ? theme.colors.accent : theme.colors.accentSecondary,
                opacity,
                transform: [{ translateY }],
              },
            ]}
          />
        );
      }) : null}
    </View>
  );
});

const TonyteBackground: React.FC<Props> = ({ children, style, appearanceAware = false }) => {
  const { theme } = useTheme();
  const showDaylight = appearanceAware && !theme.isDark;
  return (
    <View style={[styles.root, showDaylight && { backgroundColor: theme.colors.background }, style]}>
      {showDaylight ? <DaylightBackdrop /> : <><AmbientGlowSystem /><IdentityLayer /></>}
      <SeasonalLayer />
      {children}
    </View>
  );
};

const ambientWidth = width * 1.44;
const ambientHeight = height * 1.44;
const androidTopGlowSize = Math.max(280, Math.min(width, height) * 0.86);
const androidTopDepthSize = androidTopGlowSize * 0.68;
const androidBottomGlowSize = Math.max(360, Math.min(width, height) * 1.08);
const androidBottomTealSize = androidBottomGlowSize * 0.72;
const iosWashSize = Math.max(width, height) * 0.86;
const ambientTealSize = Math.max(width, height) * 0.34;
const ambientOrangeSize = Math.max(width, height) * 0.3;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Colors.bg,
    overflow: 'hidden',
  },
  androidTopGlow: {
    position: 'absolute',
    width: androidTopGlowSize,
    height: androidTopGlowSize,
    borderRadius: androidTopGlowSize / 2,
    left: -androidTopGlowSize * 0.48,
    top: -androidTopGlowSize * 0.28,
    backgroundColor: 'rgba(38,208,206,0.085)',
    filter: [{ blur: 46 }],
  },
  androidTopDepth: {
    position: 'absolute',
    width: androidTopDepthSize,
    height: androidTopDepthSize,
    borderRadius: androidTopDepthSize / 2,
    left: -androidTopDepthSize * 0.28,
    top: androidTopDepthSize * 0.12,
    backgroundColor: 'rgba(67,94,235,0.052)',
    filter: [{ blur: 38 }],
  },
  androidBottomGlow: {
    position: 'absolute',
    width: androidBottomGlowSize,
    height: androidBottomGlowSize,
    borderRadius: androidBottomGlowSize / 2,
    right: -androidBottomGlowSize * 0.44,
    bottom: -androidBottomGlowSize * 0.34,
    backgroundColor: 'rgba(242,125,38,0.062)',
    filter: [{ blur: 56 }],
  },
  androidBottomTealLift: {
    position: 'absolute',
    width: androidBottomTealSize,
    height: androidBottomTealSize,
    borderRadius: androidBottomTealSize / 2,
    right: -androidBottomTealSize * 0.28,
    bottom: androidBottomTealSize * 0.02,
    backgroundColor: 'rgba(38,208,206,0.038)',
    filter: [{ blur: 42 }],
  },
  iosTealWash: {
    position: 'absolute',
    width: iosWashSize,
    height: iosWashSize * 0.76,
    borderRadius: iosWashSize / 2,
    left: -iosWashSize * 0.42,
    top: -iosWashSize * 0.18,
    backgroundColor: 'rgba(38,208,206,0.058)',
    filter: [{ blur: 72 }],
    transform: [{ rotate: '-12deg' }, { scaleX: 1.35 }],
  },
  iosNavyWash: {
    position: 'absolute',
    width: iosWashSize * 1.08,
    height: iosWashSize * 0.82,
    borderRadius: iosWashSize / 2,
    right: -iosWashSize * 0.52,
    top: height * 0.2,
    backgroundColor: 'rgba(67,94,235,0.036)',
    filter: [{ blur: 78 }],
    transform: [{ rotate: '16deg' }, { scaleX: 1.28 }],
  },
  iosTopSheen: {
    position: 'absolute',
    left: -width * 0.2,
    right: -width * 0.2,
    top: -height * 0.08,
    height: height * 0.44,
    backgroundColor: 'rgba(255,255,255,0.012)',
    filter: [{ blur: 34 }],
    transform: [{ rotate: '-7deg' }],
  },
  ambientLayer: {
    position: 'absolute',
    left: -width * 0.22,
    top: -height * 0.22,
    width: ambientWidth,
    height: ambientHeight,
    opacity: Platform.OS === 'ios' ? 0.34 : 0.5,
    filter: [{ blur: Platform.OS === 'ios' ? 18 : 16 }],
  },
  ambientTeal: {
    position: 'absolute',
    width: ambientTealSize,
    height: ambientTealSize * 0.72,
    borderRadius: ambientTealSize / 2,
    left: ambientWidth * 0.32 - ambientTealSize / 2,
    top: ambientHeight * 0.22 - (ambientTealSize * 0.72) / 2,
    backgroundColor: Platform.OS === 'ios' ? 'rgba(38,208,206,0.036)' : 'rgba(38,208,206,0.07)',
    transform: [{ scaleX: 1.22 }],
  },
  ambientOrange: {
    position: 'absolute',
    width: ambientOrangeSize,
    height: ambientOrangeSize * 0.72,
    borderRadius: ambientOrangeSize / 2,
    left: ambientWidth * 0.78 - ambientOrangeSize / 2,
    top: ambientHeight * 0.7 - (ambientOrangeSize * 0.72) / 2,
    backgroundColor: Platform.OS === 'ios' ? 'rgba(242,125,38,0.028)' : 'rgba(242,125,38,0.048)',
    transform: [{ scaleX: 1.18 }],
  },
  dotsLayer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0.16,
  },
  dot: {
    position: 'absolute',
    width: 2,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.24)',
  },
  route: {
    position: 'absolute',
    left: width * 0.1,
    top: height * 0.18,
    width: width * 0.8,
    height: height * 0.46,
    opacity: 0.16,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderColor: 'rgba(38,208,206,0.5)',
    borderStyle: 'dashed',
    borderTopRightRadius: 120,
    transform: [{ rotate: '-8deg' }],
  },
  routeEnd: {
    position: 'absolute',
    left: width * 0.87,
    top: height * 0.16,
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#26d0ce',
    shadowColor: '#26d0ce',
    shadowOpacity: 0.55,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 0 },
    elevation: 8,
  },
  floatPin: {
    position: 'absolute',
    left: Math.max(22, width * 0.12),
    top: height * 0.2,
    opacity: 0.72,
    textShadowColor: 'rgba(0,0,0,0.28)',
    textShadowOffset: { width: 0, height: 10 },
    textShadowRadius: 22,
    transform: [{ rotate: '-4deg' }],
  },
  floatCompass: {
    position: 'absolute',
    right: Math.max(24, width * 0.13),
    bottom: height * 0.18,
    opacity: 0.72,
    textShadowColor: 'rgba(0,0,0,0.28)',
    textShadowOffset: { width: 0, height: 10 },
    textShadowRadius: 22,
    transform: [{ rotate: '4deg' }],
  },
  centerVignette: {
    position: 'absolute',
    left: width * 0.5 - Math.max(width, height) * 0.75,
    top: height * 0.4 - Math.max(width, height) * 0.75,
    width: Math.max(width, height) * 1.5,
    height: Math.max(width, height) * 1.5,
    borderRadius: Math.max(width, height) * 0.75,
    borderWidth: Math.max(width, height) * 0.24,
    borderColor: 'rgba(3,7,15,0.34)',
  },
});

const seasonalStyles = StyleSheet.create({
  backgroundArtwork: { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, opacity: 0.14 },
  headerArtwork: { position: 'absolute', top: 0, left: 0, right: 0, height: Math.min(240, height * 0.25), opacity: 0.22 },
  illustration: { position: 'absolute', top: 72, right: 18, width: 104, height: 104, opacity: 0.72 },
  particle: { position: 'absolute' },
});

const daylightStyles = StyleSheet.create({
  tealWash: {
    position: 'absolute',
    width: Math.max(width, height) * 0.72,
    height: Math.max(width, height) * 0.52,
    borderRadius: Math.max(width, height),
    left: -width * 0.56,
    top: -height * 0.12,
    backgroundColor: 'rgba(38,208,206,0.055)',
    filter: [{ blur: 58 }],
    transform: [{ rotate: '-12deg' }],
  },
  orangeWash: {
    position: 'absolute',
    width: Math.max(width, height) * 0.68,
    height: Math.max(width, height) * 0.5,
    borderRadius: Math.max(width, height),
    right: -width * 0.62,
    bottom: -height * 0.06,
    backgroundColor: 'rgba(242,125,38,0.05)',
    filter: [{ blur: 64 }],
    transform: [{ rotate: '14deg' }],
  },
  dotsLayer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0.12,
  },
  dot: {
    backgroundColor: 'rgba(93,82,68,0.22)',
  },
  orbitLarge: {
    position: 'absolute',
    width: width * 1.18,
    height: width * 1.18,
    borderRadius: width,
    borderWidth: 1,
    borderColor: 'rgba(21,155,154,0.065)',
    right: -width * 0.72,
    top: height * 0.14,
  },
  orbitSmall: {
    position: 'absolute',
    width: width * 0.72,
    height: width * 0.72,
    borderRadius: width,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.055)',
    left: -width * 0.42,
    bottom: height * 0.08,
  },
});

export default TonyteBackground;
